import React from 'react';
import cx from 'classnames';
import { uid } from 'react-uid';
import get from 'lodash/get';
import { Link as RouterLink } from 'react-router-dom';

import Cta from 'core/components/Cta';
import { useDropdown, useMemberContext, useTrackEvent } from 'core/hooks';
import { Icon, ICONS } from 'core/components/Icon';
import { LOGOUT_STATUS, SITECORE_PAGES } from 'core/constants';
import {
	clearSessionCookie,
	setDisplayLogoutSuccessFlag,
} from 'core/browserStorage';

import * as S from './AdditionalLinksGroup.style';
import { IFields as IHeaderFields } from '../../Header.sitecore';
import Notifications, {
	IProps as INotificationsProps,
} from './subcomponents/Notifications';

interface IProfileLinksProps
	extends Pick<IHeaderFields, 'secondaryMenuProfileLinks'> {
	handleClick: () => void;
	dataTargetRoot: string;
	secondaryMenuProfileItemId?: string;
}

export const ProfileLinks = ({
	handleClick,
	dataTargetRoot,
	secondaryMenuProfileLinks,
	secondaryMenuProfileItemId,
}: IProfileLinksProps) => {
	// Analytics spreadsheet:
	// - Section: 'Header'
	// - ID: 1.1.4
	const { trackEvent } = useTrackEvent(
		{
			event_section: 'navigation',
			event_type: 'header',
			event_title: 'authenticated',
		},
		false,
	);

	/**
	 * Track the clicking event and toggle the dropdown off.
	 */
	const handleClickWithTrack = React.useCallback(
		({ text, href }) => () => {
			// Analytics spreadsheet:
			// - Section: 'Header'
			// - ID: 1.1.4
			trackEvent({
				event_section: 'navigation',
				event_type: 'header',
				event_title: 'authenticated',
				event_label: text?.toLowerCase(),
				event_trigger: 'click link',
				event_url: href,
			});

			handleClick();
		},
		[handleClick, trackEvent],
	);

	return (
		<>
			{secondaryMenuProfileLinks.map(
				({ id, fields: { link, icon } }, index) => (
					<li
						key={uid({ id, index })}
						className={cx({
							active: secondaryMenuProfileItemId === id,
						})}
					>
						<Cta
							ctaStyle="plain"
							size="small"
							jssLink={link as any}
							icon={icon.value}
							iconProps={{ size: 24 }}
							iconAlign="left"
							onClick={handleClickWithTrack({ ...link?.value })}
							data-target-id={`${dataTargetRoot}--${id}`}
						/>
					</li>
				),
			)}
		</>
	);
};

export interface IProps {
	profile: Pick<
		IHeaderFields,
		'level1ProfileButton' | 'secondaryMenuProfileLinks'
	>;
	notifications: INotificationsProps;
	dataTargetRoot?: string;
}

const AdditionalLinks = ({
	profile,
	notifications,
	dataTargetRoot = 'desktop--header--additional-links',
}: IProps) => {
	const { first_name: memberFirstName } = useMemberContext();
	const { level1ProfileButton, secondaryMenuProfileLinks } = profile;

	const profileRef = React.useRef<HTMLDivElement>();
	const {
		Dropdown: ProfileDropdown,
		isExpanded: isProfileExpanded,
		toggleDropdown: toggleProfileDropdown,
	} = useDropdown(profileRef);

	const getLogoutDetails = get(
		secondaryMenuProfileLinks,
		'fields?.link?.value',
	);

	const filteredSecondaryLinks = secondaryMenuProfileLinks.filter(
		items =>
			items?.fields?.link?.value?.href?.toLowerCase() !== SITECORE_PAGES.LOGOUT,
	);

	const handleLogout = () => {
		sessionStorage.clear();
		localStorage.clear();
		clearSessionCookie();
		setDisplayLogoutSuccessFlag(LOGOUT_STATUS.LOGOUT);
		window.location.assign(getLogoutDetails?.href || SITECORE_PAGES.LOGOUT);
	};

	return (
		<S.Wrapper>
			<S.BtnWrapper>
				<RouterLink
					to={getLogoutDetails?.href || SITECORE_PAGES.LOGOUT}
					onClick={() => handleLogout}
				>
					<Cta
						ctaType="button"
						ctaStyle="plain"
						icon={ICONS.EXIT_TO_APP}
						iconAlign="right"
						data-target-id={`${dataTargetRoot}--logout-btn`}
					>
						{getLogoutDetails?.text || 'Logout'}
					</Cta>
				</RouterLink>
			</S.BtnWrapper>
			<Notifications
				{...notifications}
				dataTargetRoot={`${dataTargetRoot}--notifications`}
			/>
			<S.BtnWrapper ref={profileRef as any} role="menu">
				<Cta
					ctaType="button"
					ctaStyle="plain"
					icon={ICONS.CHEVRON_DOWN}
					iconProps={{ rotate: isProfileExpanded ? 180 : 0, size: 12 }}
					onClick={toggleProfileDropdown}
					aria-expanded={isProfileExpanded}
					data-target-id={`${dataTargetRoot}--profile-btn`}
				>
					<Icon name={level1ProfileButton.value} size={24} />
					{`Hi, `}
					{memberFirstName}
				</Cta>
				<ProfileDropdown topOffset={65} alignment="right">
					<S.Ul data-target-id={`${dataTargetRoot}--profile-panel`}>
						<ProfileLinks
							dataTargetRoot={`${dataTargetRoot}--profile-panel`}
							secondaryMenuProfileLinks={filteredSecondaryLinks}
							handleClick={toggleProfileDropdown}
						/>
					</S.Ul>
				</ProfileDropdown>
			</S.BtnWrapper>
		</S.Wrapper>
	);
};

export default AdditionalLinks;
